import languages from "@/data/languages.json";
import { resolveBrowserLanguageCode } from "./browser-language";

export const ACQUISITION_DEMO_FALLBACK_INSTRUCTION_LANGUAGE_CODE = "en-US";

export type AcquisitionDemoLanguageSource = "route" | "saved" | "browser" | "fallback";

export type AcquisitionDemoLanguageOption = {
  code: string;
  name: string;
};

export type ResolvedAcquisitionDemoLanguage = AcquisitionDemoLanguageOption & {
  source: AcquisitionDemoLanguageSource;
};

export type AcquisitionDemoConfig = {
  targetLanguage: ResolvedAcquisitionDemoLanguage | null;
  instructionLanguage: ResolvedAcquisitionDemoLanguage;
  languageLevel: "beginner";
  supportedLanguages: readonly AcquisitionDemoLanguageOption[];
};

export type ResolveAcquisitionDemoConfigInput = {
  routeTargetLanguageCode?: string | null;
  savedTargetLanguageCode?: string | null;
  routeInstructionLanguageCode?: string | null;
  savedInstructionLanguageCode?: string | null;
  browserLanguages?: readonly string[] | null;
};

const languageMap = languages as Record<string, string>;

export const acquisitionDemoLanguages: readonly AcquisitionDemoLanguageOption[] = Object.freeze(
  Object.entries(languageMap).map(([name, code]) => Object.freeze({ code, name }))
);

const languageByNormalizedCode = new Map(
  acquisitionDemoLanguages.map((language) => [language.code.toLowerCase(), language])
);

const preferredBrowserLanguageCodesByBase: Readonly<Record<string, string>> = {
  en: ACQUISITION_DEMO_FALLBACK_INSTRUCTION_LANGUAGE_CODE,
  zh: "cmn-CN",
};

const preferredBrowserLanguageCodesByLocale: Readonly<Record<string, string>> = {
  "zh-hk": "yue-HK",
  "zh-mo": "yue-HK",
};

export function getAcquisitionDemoLanguage(
  languageCode: string | null | undefined
): AcquisitionDemoLanguageOption | null {
  if (!languageCode?.trim()) return null;
  return languageByNormalizedCode.get(languageCode.trim().toLowerCase()) ?? null;
}

export function isSupportedAcquisitionDemoLanguageCode(
  languageCode: string | null | undefined
): boolean {
  return getAcquisitionDemoLanguage(languageCode) !== null;
}

function resolveCandidate(
  candidates: ReadonlyArray<{
    code: string | null | undefined;
    source: AcquisitionDemoLanguageSource;
  }>
): ResolvedAcquisitionDemoLanguage | null {
  for (const candidate of candidates) {
    const language = getAcquisitionDemoLanguage(candidate.code);
    if (language) {
      return { ...language, source: candidate.source };
    }
  }

  return null;
}

/**
 * Resolves acquisition-demo defaults without reading browser or storage globals.
 * Generic pages intentionally leave the target unset so a first-time visitor
 * chooses what they want to practise.
 */
export function resolveAcquisitionDemoConfig({
  browserLanguages,
  routeInstructionLanguageCode,
  routeTargetLanguageCode,
  savedInstructionLanguageCode,
  savedTargetLanguageCode,
}: ResolveAcquisitionDemoConfigInput = {}): AcquisitionDemoConfig {
  const targetLanguage = resolveCandidate([
    { code: routeTargetLanguageCode, source: "route" },
    { code: savedTargetLanguageCode, source: "saved" },
  ]);

  const explicitInstructionLanguage = resolveCandidate([
    { code: routeInstructionLanguageCode, source: "route" },
    { code: savedInstructionLanguageCode, source: "saved" },
  ]);
  const browserMatch = resolveBrowserLanguageCode({
    browserLanguages,
    preferredLanguageCodesByBase: preferredBrowserLanguageCodesByBase,
    preferredLanguageCodesByLocale: preferredBrowserLanguageCodesByLocale,
    supportedLanguageCodes: acquisitionDemoLanguages.map((language) => language.code),
  });
  const browserInstructionLanguage = browserMatch
    ? resolveCandidate([{ code: browserMatch.languageCode, source: "browser" }])
    : null;
  const fallbackInstructionLanguage = resolveCandidate([
    {
      code: ACQUISITION_DEMO_FALLBACK_INSTRUCTION_LANGUAGE_CODE,
      source: "fallback",
    },
  ]);

  if (!fallbackInstructionLanguage) {
    throw new Error("The acquisition demo fallback instruction language is not configured.");
  }

  return {
    instructionLanguage:
      explicitInstructionLanguage ?? browserInstructionLanguage ?? fallbackInstructionLanguage,
    languageLevel: "beginner",
    supportedLanguages: acquisitionDemoLanguages,
    targetLanguage,
  };
}
